import React, { useCallback, useState } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import ActionButton from 'components/action-button';
import { MobileSpacer } from 'components/mobile-spacer';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import useErrorModal from 'hooks/use-error-modal';
import { AddGiftCardInput } from 'pages/cart/payment/add-gift-card-input';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { usePaymentContext } from 'state/payment';
import { useUIContext } from 'state/ui';
import noop from 'utils/noop';
import { relativeRoutes } from 'utils/routing';

import { GIFT_CARD_NUMBER_TRANSLATION_ID } from '../../add-inline-gift-card/constants';
import GiftCardImage from '../gift-card-image';
import { BalanceDisclaimer, FormContainer } from '../styled';
import { useCardNumber } from '../use-giftcard-number';

const AddGiftCardModal: React.FC<{
  onCardAdded: (fdAccountId?: string | null) => void;
  handleSelect: (fdAccountId?: string | null) => void;
}> = ({ onCardAdded, handleSelect = noop }) => {
  const [isLoading, setIsLoading] = useState(false);
  const payment = usePaymentContext();
  const logger = useLogger();
  const { formatMessage } = useIntl();
  const { giftCardNumber, errors, isValidGiftCardNumber, handleCardNumberChange } = useCardNumber();
  const enablePrepaidDigitalCardCreation = useFlag(
    LaunchDarklyFlag.ENABLE_PREPAID_DIGITAL_CARD_CREATION
  );

  const handleCloseModal = () => {
    handleSelect(payment.defaultPaymentMethodId || CurrentSelectedMethodType.ADD_NEW_CARD);
    navigate('../');
  };

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
    onCancel: handleCloseModal,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { formatCurrencyForUser } = useUIContext();

  const addNewCard = useCallback(
    async event => {
      event.preventDefault();

      setIsLoading(true);

      if (payment.getPrepaidPaymentMethod()) {
        return openErrorDialog({ message: formatMessage({ id: 'giftCardExists' }) });
      }

      try {
        const { currentBalance, fdAccountId } = await payment.mergePrepaidCardBalances({
          // Source pin is not required, BK crown cards don't have it
          sourceCardNumber: giftCardNumber,
        });

        onCardAdded(fdAccountId);

        const removeCurrentRoute = location.pathname.replace(relativeRoutes.addGiftCard, '');

        const formatCurrency = formatCurrencyForUser(currentBalance);

        navigate(`${removeCurrentRoute}/${relativeRoutes.successGiftCard}`, {
          state: { currentBalance: formatCurrency },
        });
      } catch (error) {
        openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
        logger.error({ error, message: 'Error adding gift card' });
      } finally {
        setIsLoading(false);
      }
    },
    [
      payment,
      openErrorDialog,
      formatMessage,
      giftCardNumber,
      onCardAdded,
      location.pathname,
      formatCurrencyForUser,
      navigate,
      logger,
    ]
  );

  const modalBtnText = `${formatMessage({ id: 'save' })} & ${formatMessage({ id: 'continue' })}`;

  return (
    <Modal
      onDismiss={handleCloseModal}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Add gift card',
      }}
    >
      <ModalContent>
        <ModalHeading id="modal-heading">
          {formatMessage({ id: 'addNewPaymentMethodGiftCard' })}
        </ModalHeading>
        <GiftCardImage />
        {enablePrepaidDigitalCardCreation && (
          <BalanceDisclaimer data-testid="gift-card-balance-disclaimer" aria-live="polite">
            {formatMessage({ id: 'giftCardBalanceDisclaimer' })}
          </BalanceDisclaimer>
        )}
        <FormContainer data-testid="add-giftcard-form" onSubmit={addNewCard} noValidate>
          <AddGiftCardInput
            onChange={handleCardNumberChange}
            errorMessage={errors?.giftCardNumber}
            label={formatMessage({ id: GIFT_CARD_NUMBER_TRANSLATION_ID })}
            data-testid="add-giftcard-input"
          />
          <Box margin="1rem 0">
            <ActionButton
              fullWidth
              data-testid="save-and-continue"
              isLoading={isLoading}
              disabled={!isValidGiftCardNumber}
            >
              {modalBtnText}
            </ActionButton>
          </Box>
          <MobileSpacer />
        </FormContainer>
      </ModalContent>

      <ErrorDialog />
    </Modal>
  );
};

export default AddGiftCardModal;
